/**
 * CUSTOM KIYO — kiyo-scroll-depth.js
 *
 * GA4 "scroll" event (90 %) measured on the REAL scroll container.
 *
 * Why: Heritage 4.1.3 scrolls `.page-wrapper` on viewports >= 990px (html and
 * body carry overflow: hidden, see base.css and scroll-container.js). Google's
 * Enhanced Measurement "Scrolls" only watches the window, whose document is now
 * exactly one viewport tall on desktop — so its 90 % threshold is met at page
 * load, and every desktop page view counted as "scrolled" since 2026-09-01
 * (desktop scroll/page_view 0.15 -> 0.50, THEME-CHANGES.md 2026-09-02).
 *
 * This script replaces the built-in measurement on every device: same event
 * name and parameter as Google's (scroll / percent_scrolled: 90), fired once
 * per page load when 90 % of the scroll container's height has been seen.
 *
 * REQUIRES: Enhanced Measurement "Scrolls" switched OFF in the GA4 web data
 * stream (Property 326441718, G-QPXHCDEJ1N). With it on, desktop double-counts
 * (built-in at load + this one at 90 %) and mobile counts twice at 90 %.
 *
 * Container choice mirrors scroll-container.js: `.page-wrapper` while
 * (min-width: 990px) matches, otherwise document.scrollingElement. Re-bound on
 * breakpoint change. Element scroll events do not bubble, so the listener has
 * to sit on the container itself; window covers the mobile/document case.
 *
 * Short pages (content shorter than 90 % of the viewport) fire at load — that
 * is Google's own behaviour for the built-in event and keeps the semantics.
 *
 * Fail-safe: no gtag, no page wrapper, or an exception -> nothing happens;
 * nothing else on the page depends on this file.
 */
(() => {
  if (window.__kiyoScrollDepth) return;
  window.__kiyoScrollDepth = true;

  const THRESHOLD = 0.9;
  const PERCENT = 90;
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  let fired = false;
  let bound = null; // element currently carrying the scroll listener
  let frame = null;

  const container = () => {
    if (desktopQuery.matches) {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  };

  // Same call shape as Google's snippet: a queued `arguments` object is picked
  // up by gtag.js if it has not loaded yet; a live gtag sends right away.
  const send = () => {
    const params = { percent_scrolled: PERCENT };
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'scroll', params);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    // Queue exactly like Google's own snippet: an `arguments` object, which
    // gtag.js replays once it has loaded (plain arrays are ignored).
    (function () {
      window.dataLayer.push(arguments);
    })('event', 'scroll', params);
  };

  const check = () => {
    frame = null;
    if (fired) return;
    const el = container();
    const seen = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight;
    if (!total || seen / total < THRESHOLD) return;
    fired = true;
    unbind();
    try {
      send();
    } catch (error) {
      // Analytics must never break the page.
    }
  };

  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(check);
  };

  const unbind = () => {
    if (!bound) return;
    bound.removeEventListener('scroll', schedule);
    bound = null;
  };

  const bind = () => {
    unbind();
    if (fired) return;
    const el = container();
    // The document scrolls via the window; an element emits its own events.
    bound = el === document.scrollingElement || el === document.documentElement ? window : el;
    bound.addEventListener('scroll', schedule, { passive: true });
    schedule();
  };

  desktopQuery.addEventListener('change', bind);
  window.addEventListener('resize', schedule, { passive: true });

  if (document.readyState === 'complete') {
    bind();
  } else {
    // Bind at DOMContentLoaded so early scrolling counts; re-check at load
    // once images and lazy sections have taken their final height.
    document.addEventListener('DOMContentLoaded', bind, { once: true });
    window.addEventListener('load', schedule, { once: true });
  }
})();
